import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ChainId, SearchResult } from '@pleco/shared';
import { CHAINS, formatUsd, isValidAddress, shortAddress } from '@pleco/shared';
import { api } from '../lib/api.js';
import { useChains } from '../lib/chains.js';
import { IconSearch } from './Icons.js';

/**
 * Omnibox: paste a contract address (any supported chain) or search by name.
 * Pasting a raw address short-circuits the API and jumps straight to the map.
 */
export function SearchCommand({ variant = 'header', locked = false }: { variant?: 'header' | 'hero'; locked?: boolean }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const [busy, setBusy] = useState(false);
  const { defaultChain, evmChains, hasEvm, hasSolana, summary } = useChains();
  const [chainHint, setChainHint] = useState<ChainId | undefined>();
  /** Signed-out visitors can paste an address (sign-in follows); name search needs a session. */
  const [lockedHint, setLockedHint] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (variant === 'header' && (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
      if (e.key === 'Escape') setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [variant]);

  useEffect(() => {
    const term = q.trim();
    setLockedHint(false);
    if (term.length < 2 || locked) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setBusy(true);
    const id = setTimeout(async () => {
      try {
        const { results } = await api.search(term);
        if (!cancelled) {
          setResults(results);
          setCursor(0);
          setOpen(true);
        }
      } catch {
        /* search is best-effort; pasting an address still works */
      } finally {
        if (!cancelled) setBusy(false);
      }
    }, 260);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [q, locked]);

  const go = (chain: ChainId, address: string) => {
    setOpen(false);
    setQ('');
    navigate(`/map/${chain}/${address}`);
  };

  const submit = () => {
    const term = q.trim();
    if (!term) return;
    if (term.toLowerCase().startsWith('demo')) return go(defaultChain, term.toLowerCase());
    // Raw address? Guess the chain family and jump directly.
    if (hasEvm && /^0x[0-9a-fA-F]{40}$/.test(term)) return go(chainHint ?? evmChains[0]!, term.toLowerCase());
    if (hasSolana && isValidAddress('sol', term)) return go('sol', term);
    if (locked) return setLockedHint(true);
    const r = results[cursor];
    if (r) go(r.chain, r.address);
  };

  const looksEvm = /^0x[0-9a-fA-F]{40}$/.test(q.trim());
  const isRawEvm = hasEvm && looksEvm;
  // An EVM address pasted while only Solana is enabled: say so instead of a silent miss.
  const evmDisabled = !hasEvm && looksEvm;

  return (
    <div className={`tm-search is-${variant}`} ref={boxRef}>
      <IconSearch className="tm-search-icon" size={variant === 'hero' ? 20 : 16} />
      <input
        ref={inputRef}
        className="tm-search-input"
        placeholder={variant === 'hero' ? 'Token address or name' : 'Search or paste a token address'}
        value={q}
        spellCheck={false}
        autoComplete="off"
        enterKeyHint="go"
        aria-label="Search tokens"
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
          if (e.key === 'ArrowDown') setCursor((c) => Math.min(results.length - 1, c + 1));
          if (e.key === 'ArrowUp') setCursor((c) => Math.max(0, c - 1));
        }}
      />
      {busy && <span className="spinner tm-search-spin" />}
      {variant === 'header' && !q && !busy && <span className="tm-kbd">Ctrl K</span>}
      {variant === 'hero' && (
        <button className="tm-btn primary tm-search-go" onClick={() => (q.trim() ? submit() : inputRef.current?.focus())}>
          Map it
        </button>
      )}

      {lockedHint && !evmDisabled && (
        <div className="search-results">
          <div className="faint" style={{ padding: '12px 14px', fontSize: 12.5 }}>
            Paste the token’s address to map it, or{' '}
            <a href="/signin" style={{ color: 'var(--brand)' }}>
              sign in
            </a>{' '}
            to search by name.
          </div>
        </div>
      )}

      {evmDisabled && (
        <div className="search-results">
          <div className="faint" style={{ padding: '12px 14px', fontSize: 12 }}>
            That looks like an EVM address — this server currently maps {summary} only.
          </div>
        </div>
      )}

      {open && !evmDisabled && (isRawEvm || results.length > 0) && (
        <div className="search-results">
          {isRawEvm && (
            <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
              <div className="faint" style={{ fontSize: 11, marginBottom: 7 }}>
                OPEN {shortAddress(q.trim(), 8, 6)} ON
              </div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                {evmChains.map((c) => (
                  <button
                    key={c}
                    className={`btn sm ${chainHint === c ? 'active' : ''}`}
                    onMouseEnter={() => setChainHint(c)}
                    onClick={() => go(c, q.trim().toLowerCase())}
                  >
                    <i
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: 99,
                        background: CHAINS[c].color,
                        display: 'inline-block',
                      }}
                    />
                    {CHAINS[c].label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {results.map((r, i) => (
            <div
              key={`${r.chain}:${r.address}`}
              className={`search-item ${i === cursor ? 'sel' : ''}`}
              onMouseEnter={() => setCursor(i)}
              onClick={() => go(r.chain, r.address)}
            >
              {r.logoUrl ? (
                <img className="token-logo" src={r.logoUrl} alt="" />
              ) : (
                <div className="token-logo">{r.symbol.slice(0, 3)}</div>
              )}
              <div style={{ minWidth: 0 }}>
                <div className="sym">
                  {r.symbol}{' '}
                  <span className="chip" style={{ color: CHAINS[r.chain]?.color }}>
                    {CHAINS[r.chain]?.label ?? r.chain}
                  </span>
                </div>
                <div className="nm">{r.name}</div>
              </div>
              <div className="right">
                {r.marketCapUsd ? <div>{formatUsd(r.marketCapUsd)}</div> : null}
                {r.liquidityUsd ? <div className="faint">liq {formatUsd(r.liquidityUsd)}</div> : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
